'use client'

import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import { RevealH1, RevealP, RevealSection } from '@/components/RevealSection'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  redirectToMembershipCheckout,
  shouldAutoStartMembershipCheckout,
} from '@/constants/planSignup'
import { useLenisContext } from '@/providers/Lenis'

import { DesktopTierDisplay } from './memberships/DesktopTierDisplay'
import { MobileTierDisplay } from './memberships/MobileTierDisplay'
import SignUpForm from './SignUpForm'

import type { JoinButtonIntent } from './memberships/types'
import type { MembershipLevel, User } from '@/payload-types'

const HEADING = 'Join the MAHA Movement'
const DESCRIPTION =
  "Your membership funds research, events, newsletters & the people fighting for your health. Let's Make America Healthy Again."

export type MembershipProps = {
  tiers: MembershipLevel[]
  user: User | null
  userMemberLevel: MembershipLevel | null
}

export default function Memberships({ tiers, user, userMemberLevel }: MembershipProps) {
  const { isMobile } = useLenisContext()
  const searchParams = useSearchParams()
  const checkoutStartedRef = useRef(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [contactDialogOpen, setContactDialogOpen] = useState(false)
  const [signUpModalOpen, setSignUpModalOpen] = useState(false)

  useEffect(() => {
    if (!user || checkoutStartedRef.current) return

    const planId = shouldAutoStartMembershipCheckout(searchParams)
    if (!planId) return

    const plan = tiers.find((tier) => tier.id === planId)
    if (!plan || plan.price <= 0) return

    checkoutStartedRef.current = true
    void redirectToMembershipCheckout(planId).catch((err) => {
      checkoutStartedRef.current = false
      setCheckoutError(err instanceof Error ? err.message : 'Failed to start checkout')
    })
  }, [searchParams, tiers, user])

  const onJoinClick = async (chosenPlan: MembershipLevel, intent: JoinButtonIntent) => {
    switch (intent) {
      case 'register': {
        if (user && chosenPlan.price > 0) {
          setCheckoutError(null)
          try {
            await redirectToMembershipCheckout(chosenPlan.id)
          } catch (err) {
            setCheckoutError(err instanceof Error ? err.message : 'Failed to start checkout')
          }
          break
        }
        if (!user) {
          setSignUpModalOpen(true)
        }
        break
      }
      case 'contact':
        setContactDialogOpen(true)
        break
      case 'leave-waitlist':
        break
      case 'join-waitlist':
        break
    }
  }

  return (
    <div className="relative z-0">
      <div className="fixed inset-0 -z-10 size-full bg-[url(/media/membership_bg.svg)] bg-top bg-repeat" />
      <div className="mx-auto mb-4 max-w-xl space-y-4 text-center lg:mb-10">
        <RevealH1 once className="font-display text-3xl font-bold md:text-5xl">
          {HEADING}
        </RevealH1>
        <RevealP once className="md:text-lg">
          {DESCRIPTION}
        </RevealP>
        {checkoutError && (
          <p className="rounded-md bg-red-50 p-3 text-sm text-red-600">{checkoutError}</p>
        )}
      </div>
      {isMobile ? (
        <MobileTierDisplay
          tiers={tiers}
          onJoinClick={onJoinClick}
          user={user}
          userMemberLevel={userMemberLevel}
        />
      ) : (
        <DesktopTierDisplay
          tiers={tiers}
          onJoinClick={onJoinClick}
          user={user}
          userMemberLevel={userMemberLevel}
        />
      )}
      <RevealSection once className="mx-auto mt-4 max-w-3xl md:mt-7">
        <div className="payload-richtext prose md:prose-md dark:prose-invert mx-auto max-w-none space-y-4 text-center font-body text-sm text-muted-foreground">
          <p>
            <b>About MAHA Membership: </b>This membership program is advertised and presented by
            MAHA Action, Inc., a 501(c)(4) social welfare organization. When you enroll, your
            membership agreement is with MAHA Holdings, Inc. (&quot;MAHA Holdings&quot;), a separate
            affiliated 501(c)(4) entity that administers the program and collects and retains all
            membership dues. Membership dues are not tax-deductible as charitable contributions and
            are not political contributions to any candidate or committee. Donations to MAHA Action
            are separate from MAHA Membership enrollment and are processed through a different
            channel.
          </p>
          <p>
            *MAHA Summit: non-Leadership Circle tiers are eligible subject to capacity and RSVP
            requirements; Leadership Circle includes a reserved allocation. Member events are
            guaranteed first-come, first-served by tier when capacity is limited. Guaranteed access
            is subject to capacity, event occurrence, and MAHA&apos;s right to modify benefits with
            notice per Terms of Service. Paid memberships renew automatically; cancel anytime to
            stop renewal and your membership stays active through the end of your current term (no
            proration). By joining, you agree to MAHA&apos;s{' '}
            <Link href="/terms-of-service" target="_blank">
              Terms
            </Link>{' '}
            &{' '}
            <Link href="/privacy-policy" target="_blank">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </RevealSection>
      <RevealSection once className="mx-auto mt-7 max-w-3xl">
        <div className="payload-richtext prose md:prose-md dark:prose-invert mx-auto max-w-none text-center font-body text-sm text-primary">
          <p>
            For any support questions, please reach out to{' '}
            <a href="mailto:memberships@mahamember.org">memberships@mahamember.org</a>.
          </p>
        </div>
      </RevealSection>
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="prose">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Contact Us</DialogTitle>
          </DialogHeader>
          <DialogDescription className="font-body">
            For member support, please email{' '}
            <a href="mailto:memberships@mahamember.org">memberships@mahamember.org</a>
          </DialogDescription>
        </DialogContent>
      </Dialog>
      <Dialog open={signUpModalOpen} onOpenChange={setSignUpModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl gap-0 overflow-y-auto p-0 sm:rounded-2xl">
          <DialogTitle className="sr-only">Create your account</DialogTitle>
          <SignUpForm variant="modal" />
        </DialogContent>
      </Dialog>
    </div>
  )
}
